import React from "react";
import { styled } from "@mui/material/styles";
import Switch from "@mui/material/Switch";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

export interface CustomCheckboxProps {
  fieldName: string;
  value: boolean;
  onChange: (data: { fieldName: string; value: boolean }) => void;
  label?: string;
  disabled?: boolean;
  variant?: "default" | "ios";
}

// iOS-style switch component
const IOSSwitch = styled((props: any) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 48,
  height: 24,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 0,
    margin: 2,
    transitionDuration: "300ms",
    "&.Mui-checked": {
      transform: "translateX(24px)",
      color: "#fff",
      "& + .MuiSwitch-track": {
        backgroundColor: "#0B9DBD",
        opacity: 1,
        border: 0,
      },
      "&.Mui-disabled + .MuiSwitch-track": {
        opacity: 0.5,
      },
    },
    "&.Mui-focusVisible .MuiSwitch-thumb": {
      color: "#0B9DBD",
      border: "4px solid #fff",
    },
    "&.Mui-disabled .MuiSwitch-thumb": {
      color:
        theme.palette.mode === "light"
          ? theme.palette.grey[100]
          : theme.palette.grey[600],
    },
    "&.Mui-disabled + .MuiSwitch-track": {
      opacity: theme.palette.mode === "light" ? 0.7 : 0.3,
    },
  },
  "& .MuiSwitch-thumb": {
    boxSizing: "border-box",
    width: 20,
    height: 20,
    borderRadius: 10,
  },
  "& .MuiSwitch-track": {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === "light" ? "#E9E9EA" : "#39393D",
    opacity: 1,
    transition: theme.transitions.create(["background-color"], {
      duration: 500,
    }),
  },
}));

const CustomCheckboxToggle: React.FC<CustomCheckboxProps> = ({
  fieldName,
  value,
  onChange,
  label,
  disabled = false,
  variant = "default",
}) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange && !disabled) {
      onChange({
        fieldName,
        value: event.target.checked,
      });
    }
  };

  const switchComponent =
    variant === "ios" ? (
      <IOSSwitch
        checked={Boolean(value)}
        onChange={handleChange}
        disabled={disabled}
      />
    ) : (
      <Switch
        sx={{
          mr: 1,
          width: 48,
          height: 24,
          padding: 0,
          "& .MuiSwitch-switchBase": {
            padding: 0,
            margin: "2px",
            "&.Mui-checked": {
              transform: "translateX(24px)",
            },
          },
          "& .MuiSwitch-thumb": {
            width: 20,
            height: 20,
          },
          "& .MuiSwitch-track": {
            borderRadius: 12,
          },
          "& .MuiSwitch-switchBase.Mui-checked": {
            color: "#0B9DBD",
          },
          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
            backgroundColor: "#0B9DBD",
          },
        }}
        checked={Boolean(value)}
        onChange={handleChange}
        disabled={disabled}
      />
    );

  return (
    <FormGroup>
      <FormControlLabel
        control={switchComponent}
        label={label}
        disabled={disabled}
      />
    </FormGroup>
  );
};

export default CustomCheckboxToggle;
